import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { Form, Input, Select, Checkbox, Tooltip, Spin, message } from 'antd';
import s from './s.module.less';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { editBasicInfo } from 'api/newUniprofile';
import useGetSettingData from 'hooks/useGetSettingData';
import { EnumFields } from 'types/enumerationData';
import FormValidationHelper from 'utils/validation';
import TitleTip from 'assets/channel/titleTip.jpg';
import CredentialTip from 'assets/channel/credentialTip.jpg';
import UsPhoneInput from 'components/form/USPhoneInput';
import GenderSelect from 'components/form/GenderSelect';
import { IBasicInfoStep } from 'types/newUniprofile';

interface BasicInfoStepProps {
    isEditing?: boolean;
    initValue?: IBasicInfoStep;
    refetch?: () => void;
}

const BasicInfoStep = forwardRef(({ isEditing = false, initValue, refetch }: BasicInfoStepProps, ref) => {
    const [credentialTypes] = useGetSettingData(EnumFields.CREDENTIAL_TYPE);
    const [form] = Form.useForm();
    const [npiRequired, setNpiRequired] = useState(true);
    const [sameAsPersonalPhone, setSameAsPersonalPhone] = useState(false);
    const [sameAsLoginEmail, setSameAsLoginEmail] = useState(false);

    useEffect(() => {
        if (initValue) {
            form.setFieldsValue({
                ...initValue,
                credential: (initValue.credential as string)?.split(','),
            });
            setSameAsPersonalPhone(initValue.tel === initValue.practicePhone);
            setSameAsLoginEmail(initValue.email === initValue.practiceEmail);
            setNpiRequired(!initValue.npiNotRequired);
        }
    }, [initValue]);

    const onSubmit = async () => {
        try {
            const values = await form.validateFields();
            const res = await editBasicInfo({
                ...values,
                credential: (values.credential as string[]).join(','),
            });
            if (res.error) {
                message.error(res.error);
            } else {
                refetch?.();
            }
        } catch (error) {
            console.error('Form validation failed:', error);
            throw error;
        }
    };

    useImperativeHandle(ref, () => ({
        onSubmit,
    }));

    const renderEditForm = () => {
        return (
            <Form
                form={form}
                layout="vertical"
                className={s.form}
            >
                <div className={s.formRow}>
                    <Form.Item
                        label={
                            <div className={s.formLabel}>Title
                                <Tooltip color="#FFFFFF" overlayInnerStyle={{ padding: '20px', width: '400px' }} title={<div className={s.tipText}>Title will appear before the name. <img src={TitleTip} /></div>}>
                                    <QuestionCircleOutlined className={s.tooltipIcon} />
                                </Tooltip>
                            </div>
                        }
                        name="title"
                        rules={[
                            { max: 10, message: 'Exceeded maximum character length of 10' },
                        ]}
                        className="flex-1"
                    >
                        <Input type="text" placeholder="Ex. Dr., Sr., Hon., Mr., Mrs., Ms." />
                    </Form.Item>

                    <Form.Item
                        name="firstName"
                        label="First name"
                        rules={[
                            { required: true, message: 'First name is a required field' },
                            { max: 50, message: 'Exceeded maximum character length of 50' },
                        ]}
                        className="flex-1"
                    >
                        <Input placeholder="Enter your first name" />
                    </Form.Item>

                    <Form.Item
                        name="lastName"
                        label="Last name"
                        rules={[
                            { required: true, message: 'First name is a required field' },
                            { max: 50, message: 'Exceeded maximum character length of 50' },
                        ]}
                        className="flex-1"
                    >
                        <Input placeholder="Enter your last name" />
                    </Form.Item>
                </div>

                <div className={s.formRow}>
                    <div className="flex-1">
                        <Form.Item
                            name="npi"
                            label="NPI number"
                            rules={[
                                {
                                    validator: (_, value) => {
                                        if (npiRequired && !value) {
                                            return Promise.reject('NPI number is a required field');
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <Input
                                placeholder="Enter your NPI number"
                                disabled={!npiRequired}
                                maxLength={10}
                            />
                        </Form.Item>
                        <Form.Item name="npiNotRequired" valuePropName="checked" style={{ marginTop: '-24px' }}>
                            <Checkbox onChange={(e) => {
                                setNpiRequired(!e.target.checked);
                                form.setFields([{
                                    name: 'npi',
                                    errors: [],
                                }]);
                            }}
                            >
                                My practice doesn&apos;t require an NPI
                            </Checkbox>
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="gender"
                        label="Gender"
                        rules={[{ required: true, message: 'Please select your gender' }]}
                        className="flex-1"
                    >
                        <GenderSelect />
                    </Form.Item>
                </div>

                <Form.Item
                    name="credential"
                    label={
                        <span className={s.formLabel}>
                            Credentials
                            <Tooltip overlayClassName={s.tipCard} color="#FFFFFF" overlayInnerStyle={{ padding: '20px', width: '400px' }} title={<div className={s.tipText}>Credentials will appear right next to your name.<img src={CredentialTip} /></div>}>
                                <QuestionCircleOutlined className={s.tooltipIcon} />
                            </Tooltip>
                        </span>
                    }
                    rules={[{ required: true, message: 'Credentials is a required field' }]}
                >
                    <Select placeholder="search and select your credentials" mode="multiple" fieldNames={{ label: 'content', value: 'dictKey' }} options={credentialTypes} />
                </Form.Item>

                <div className={s.formRow}>
                    <Form.Item
                        name="tel"
                        label={
                            <span className={s.formLabel}>
                                Personal phone number
                                <Tooltip overlayClassName={s.tipCard} color="#FFFFFF" title={<div className={s.tipText2}>Only used for Kiwi Ops to support you; will not display to the public.</div>}>
                                    <QuestionCircleOutlined className={s.tooltipIcon} />
                                </Tooltip>
                            </span>
                        }
                        rules={[
                            { required: true, message: 'Personal phone number is a required field' },
                            FormValidationHelper.validateUSPhoneNumber('Please enter a 10-digit phone number'),
                        ]}
                        className="flex-1"
                    >
                        <UsPhoneInput />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={
                            <span className={s.formLabel}>
                                Login email address
                                <Tooltip overlayClassName={s.tipCard} color="#FFFFFF" title={<div className={s.tipText2}>Used to log in to your account. Not shown to patients.</div>}>
                                    <QuestionCircleOutlined className={s.tooltipIcon} />
                                </Tooltip>
                            </span>
                        }
                        rules={[
                            { required: true, message: 'Login email address is a required field' },
                            { type: 'email', message: 'Please enter a valid email address' },
                        ]}
                        className={s.formItemHalf}
                    >
                        <Input type="text" disabled />
                    </Form.Item>
                </div>

                <div className={s.formRow}>
                    <div className="flex-1">
                        <Form.Item
                            name="practicePhone"
                            label={
                                <span className={s.formLabel}>
                                    Practice phone number
                                    <Tooltip overlayClassName={s.tipCard} color="#FFFFFF" title={<div className={s.tipText2}>This can be the same as your personal phone number.</div>}>
                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                    </Tooltip>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Practice phone number is a required field' },
                                FormValidationHelper.validateUSPhoneNumber('Please enter a 10-digit phone number'),
                            ]}
                            className={s.formItemHalf}
                        >
                            <UsPhoneInput disabled={sameAsPersonalPhone} />
                        </Form.Item>
                        <div style={{ marginTop: '-20px' }}>
                            <Checkbox
                                checked={sameAsPersonalPhone}
                                onChange={(e) => {
                                    form.setFieldsValue({
                                        practicePhone: e.target.checked ? form.getFieldValue('tel') : '',
                                    });
                                    setSameAsPersonalPhone(e.target.checked);
                                }}
                            >
                                Same as personal phone number
                            </Checkbox>
                        </div>
                    </div>

                    <div className="flex-1">
                        <Form.Item
                            name="practiceEmail"
                            label={
                                <span className={s.formLabel}>
                                    Practice email address
                                    <Tooltip overlayClassName={s.tipCard} color="#FFFFFF" title={<div className={s.tipText2}>This can be the same as your work email.</div>}>
                                        <QuestionCircleOutlined className={s.tooltipIcon} />
                                    </Tooltip>
                                </span>
                            }
                            rules={[
                                { required: true, message: 'Practice email address is a required field' },
                                { type: 'email', message: 'Please enter a valid email address' },
                            ]}
                            className={s.formItemHalf}
                        >
                            <Input type="text" disabled={sameAsLoginEmail} />
                        </Form.Item>
                        <div style={{ marginTop: '-20px' }}>
                            <Checkbox
                                checked={sameAsLoginEmail}
                                onChange={(e) => {
                                    setSameAsLoginEmail(e.target.checked);
                                    form.setFieldsValue({
                                        practiceEmail: e.target.checked ? form.getFieldValue('email') : '',
                                    });
                                }}
                            >
                                Same as login email address
                            </Checkbox>
                        </div>
                    </div>
                </div>
            </Form>
        );
    };

    const renderSummaryView = () => {
        const { title, firstName, lastName, npi, gender, credential, tel, practicePhone, practiceEmail, email } = initValue || {};
        return (
            <div className={s.summaryView}>
                <div className={s.summaryRow}>
                    <div className={s.summaryItem}>
                        <div className={s.summaryLabel}>Title </div>
                        <div className={s.summaryValue}>{title || '-'}</div>
                    </div>

                    <div className={s.summaryItem}>
                        <div className={s.summaryLabel}>First name </div>
                        <div className={s.summaryValue}>{firstName}</div>
                    </div>

                    <div className={s.summaryItem}>
                        <div className={s.summaryLabel}>Last name </div>
                        <div className={s.summaryValue}>{lastName}</div>
                    </div>
                </div>

                <div className={s.summaryRow}>
                    <div className={s.summaryItem}>
                        <div className={s.summaryLabel}>NPI number</div>
                        <div className={s.summaryValue}>{npi}</div>
                    </div>

                    <div className={s.summaryItem}>
                        <div className={s.summaryLabel}>Gender </div>
                        <div className={s.summaryValue}>{gender}</div>
                    </div>
                </div>

                <div className={s.summaryRow}>
                    <div className={s.summaryItem}>
                        <div className={s.summaryLabel}>Credentials </div>
                        <div className={s.summaryValue}>{credential}</div>
                    </div>
                </div>

                <div className={s.summaryRow}>
                    <div className={s.summaryItem}>
                        <div className={s.summaryLabel}>Personal phone number </div>
                        <div className={s.summaryValue}>{tel}</div>
                    </div>

                    <div className={s.summaryItem}>
                        <div className={s.summaryLabel}>Login email address </div>
                        <div className={s.summaryValue}>{email}</div>
                    </div>
                </div>

                <div className={s.summaryRow}>
                    <div className={s.summaryItem}>
                        <div className={s.summaryLabel}>Practice phone number </div>
                        <div className={s.summaryValue}>{practicePhone}</div>
                    </div>

                    <div className={s.summaryItem}>
                        <div className={s.summaryLabel}>Practice email address </div>
                        <div className={s.summaryValue}>{practiceEmail}</div>
                    </div>
                </div>
            </div>
        );
    };

    return isEditing ? renderEditForm() : renderSummaryView();
});

export default BasicInfoStep;
